/**
 * Ported VERBATIM from apr_template `src/pages/IntegrationApplicationMasterData.tsx` (Planning & Integration).
 * Only import paths change, plus anything noted inline as a PORT NOTE.
 */
import React, { useState, useEffect } from 'react';
import PageHeader from '../compat/PageHeader';
import { authFetch } from '../compat/api';
import HelpPanel from '../compat/HelpPanel';
import { dispositionColor as matrixColor } from '../dispositionPalette';
import type { Help } from '../compat/helpTypes';
import './IntegrationApplicationMasterData.css';
// Decision Workspace design (owner ruling 2026-10-21): kit icons, panels and token pairs.
import { Banner, FactList, Icon, IconButton, Sheet, icons } from '@platform/ui-kit/workspace';
import { WS_CHIP, wsTone } from '../workspace/ws';

const IntegrationApplicationMasterDataHelp: Help = {
  title: 'Integration App Master Data',
  icon: '📱',
  purpose:
    'The working list of applications that carried a <strong>disposition</strong> into the Integration framework. Here you review each app&rsquo;s technology, infrastructure and dependency flag before it is grouped into a delivery wave.',
  doHere: [
    'Search by name and filter by <strong>Final Recommendation</strong> (Retire / Re-Platform / Re-Design / Retain).',
    'Click <strong>&#128065;</strong> to view an application&rsquo;s full details.',
    'Click <strong>&#9999;&#65039;</strong> to change the <strong>Application Dependencies</strong> flag (YES / NO).',
    'Check the <strong>Assignment Status</strong> column to see whether an app is already in an Integration Group.',
    'Apps already assigned are read-only (view-only) &mdash; edit them from their group instead.',
  ],
  fits: 'Integration &rarr; Grouping &amp; Scope &rarr; Integration App Master Data',
  firstTime:
    'Filter to one disposition, open a few apps with &#128065;, and set the dependency flag for any app that relies on others.',
  sections: [
    {
      type: 'defs',
      label: 'Assignment status',
      items: [
        { term: 'AVAILABLE', color: '#059669', def: 'Not yet placed in an Integration Group &mdash; free to select.' },
        { term: 'ASSIGNED', color: '#D97706', def: 'Already in a group. Retain / Re-Design apps can also be shared into other groups.' },
      ],
    },
  ],
  links: [
    { href: '/integration-framework/integration-groupings', label: 'Integration Groupings' },
    { href: '/integration-framework/application-dependencies', label: 'Application Dependencies' },
  ],
};

interface ApplicationMaster {
  application_id_code: string;
  application_name: string;
  technology_stack?: string;
  it_hardware_infrastructure?: string;
  application_dependencies?: 'YES' | 'NO';
  recommendation: 'Retire' | 'Re-Platform' | 'Re-Design' | 'Retain';
  /** The full final disposition from App Rationalization ("Re-Platform – Cloud Migration"). */
  final_disposition?: string | null;
  created_at_formatted: string;
}

interface ApplicationMasterStats {
  totalApplications: number;
  distribution: {
    'Retire': number;
    'Re-Platform': number;
    'Re-Design': number;
    'Retain': number;
  };
}

interface ApiResponse {
  success: boolean;
  data: ApplicationMaster[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  stats: ApplicationMasterStats;
}

const IntegrationApplicationMasterData: React.FC = () => {
  const [applications, setApplications] = useState<ApplicationMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<ApplicationMasterStats>({
    totalApplications: 0,
    distribution: { 'Retire': 0, 'Re-Platform': 0, 'Re-Design': 0, 'Retain': 0 }
  });
  
  // Filters and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [recommendationFilter, setRecommendationFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);
  const [sortField, setSortField] = useState('application_name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Track applications assigned to Integration Groups
  const [assignedApplications, setAssignedApplications] = useState<Set<string>>(new Set());

  // Create sorted applications with unassigned apps first
  const sortedApplications = React.useMemo(() => {
    return [...applications].sort((a, b) => {
      const aAssigned = assignedApplications.has(a.application_id_code);
      const bAssigned = assignedApplications.has(b.application_id_code);

      // If assignment status is different, unassigned comes first
      if (aAssigned !== bAssigned) {
        return aAssigned ? 1 : -1; // unassigned (false) < assigned (true)
      }

      // If same assignment status, maintain original order
      return 0;
    });
  }, [applications, assignedApplications]);
  
  // Inline editing state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<'application_dependencies' | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Modal state
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<ApplicationMaster | null>(null);
  

  // Debounce search term
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadApplicationsData = async () => {
    if (!initialLoadDone) setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
        search: debouncedSearchTerm,
        recommendationFilter,
        sortBy: sortField,
        sortOrder: sortDirection
      });

      const token = localStorage.getItem('authToken');
      const response = await authFetch(`/api/applications-master?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        // Only throw error for actual server errors, not 404
        if (response.status !== 404) {
          throw new Error('Failed to fetch applications master data');
        }
      }

      const data: ApiResponse = await response.json();

      if (data.success) {
        setApplications(data.data || []);
        setStats(data.stats || {
          totalApplications: 0,
          distribution: { 'Retire': 0, 'Re-Platform': 0, 'Re-Design': 0, 'Retain': 0 }
        });
      } else {
        // Don't treat empty data as an error
        setApplications([]);
        setStats({
          totalApplications: 0,
          distribution: { 'Retire': 0, 'Re-Platform': 0, 'Re-Design': 0, 'Retain': 0 }
        });
      }
    } catch (err: any) {
      console.error('Failed to load applications master data:', err);
      // Only set error for actual network/server errors
      if (err.message && !err.message.includes('404')) {
        setError(err.message || 'Failed to load data');
      }
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
    }
  };

  useEffect(() => {
    loadApplicationsData();
    loadAssignedApplications();
  }, [currentPage, debouncedSearchTerm, recommendationFilter, sortField, sortDirection]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const handleEditDependency = (applicationId: string, field: 'application_dependencies', currentValue: string) => {
    setEditingId(applicationId);
    setEditingField(field);
    setEditingValue(currentValue || 'NO');
  };

  const handleStartEdit = (applicationId: string) => {
    setEditingId(applicationId);
    setIsEditMode(true);
  };

  const handleCancelEditMode = () => {
    setEditingId(null);
    setEditingField(null);
    setEditingValue('');
    setIsEditMode(false);
  };

  const handleOpenChangeModal = (app: ApplicationMaster) => {
    setSelectedApplication(app);
    setShowChangeModal(true);
  };

  const handleOpenViewModal = (app: ApplicationMaster) => {
    setSelectedApplication(app);
    setShowViewModal(true);
  };

  const handleCloseChangeModal = () => {
    setShowChangeModal(false);
    setSelectedApplication(null);
    // Refresh data after changes
    loadApplicationsData();
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedApplication(null);
  };

  // Function to load applications assigned to Integration Groups
  const loadAssignedApplications = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await authFetch('/api/integration-groups/restricted-applications', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        console.error('Failed to fetch restricted applications');
        return;
      }

      const result = await response.json();
      if (result.success && result.data) {
        // Create a Set of assigned application IDs for fast lookup
        const assignedIds = new Set<string>(result.data.map((app: any) => app.application_id as string));
        setAssignedApplications(assignedIds);
      }
    } catch (error) {
      console.error('Error loading assigned applications:', error);
    }
  };

  const handleSaveDependency = async () => {
    if (!editingId || !editingField) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await authFetch(`/api/applications-master/${editingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          [editingField]: editingValue
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save dependency');
      }

      // Update the local state
      setApplications(prevApps =>
        prevApps.map(app =>
          app.application_id_code === editingId
            ? { ...app, [editingField as string]: editingValue as 'YES' | 'NO' }
            : app
        )
      );

      setEditingId(null);
      setEditingField(null);
      setEditingValue('');
    } catch (error) {
      console.error('Error saving dependency:', error);
      setError('Failed to save dependency');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingField(null);
    setEditingValue('');
    setIsEditMode(false);
  };

    // PORT NOTE: Disposition Matrix colours (§1.9.3 rule 3), not a hard-coded palette.
const getRecommendationColor = (recommendation: string) => {
    return ['Retire', 'Re-Platform', 'Re-Design', 'Retain'].includes(recommendation) ? matrixColor(recommendation).base : '#6c757d';
  };



  const totalPages = Math.ceil(stats.totalApplications / pageSize);

  if (loading) {
    return (
      <div className="page">
        <PageHeader title={<>Integration Application Master Data</>} subtitle={<>Manage applications that have been transferred to integration framework</>} />
        <div className="master-data-container">
          <div className="md-loading-state">
            <div className="md-spinner"></div>
            <p>Loading applications master data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="page-header">
          <div className="page-header__content">
            <div className="page-header__title-section">
              <h1 className="page-header__title">Integration Application Master Data</h1>
              <p className="page-header__subtitle">
                Manage applications that have been transferred to integration framework
              </p>
            </div>
          </div>
        </div>
        <div className="master-data-container">
          <div className="md-error-state">
            <div className="md-error-icon"><Icon icon={icons.AlertTriangle} size={24} /></div>
            <h3>Error Loading Data</h3>
            <p>{error}</p>
            <button className="md-button--primary" onClick={() => loadApplicationsData()}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <HelpPanel help={IntegrationApplicationMasterDataHelp} />
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Integration Application Master Data</h1>
            <p className="page-header__subtitle">
              Manage applications that have been transferred to integration framework
            </p>
          </div>
          <div className="page-header__actions">
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="master-data-container">


      {/* Filters and Search */}
      <div className="md-card">
        <div className="md-card-content">
          <div className="md-filters-row">
            <div className="md-search-field">
              <input
                type="text"
                placeholder="Search applications..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="md-input"
              />
            </div>
            
            <div className="md-filter-field">
              <select
                value={recommendationFilter}
                onChange={(e) => {
                  setRecommendationFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="md-select"
              >
                <option value="all">All Recommendations</option>
                <option value="Retire">Retire</option>
                <option value="Re-Platform">Re-Platform</option>
                <option value="Re-Design">Re-Design</option>
                <option value="Retain">Retain</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Applications Table */}
      <div className="md-card">
        <div className="md-table-container">
          {applications.length > 0 ? (
            <table className="md-table">
              <thead>
                <tr>
                  <th
                    className="md-table-header sortable"
                    onClick={() => handleSort('application_id_code')}
                  >
                    Application
                    {sortField === 'application_id_code' && (
                      <span className="sort-indicator">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th className="md-table-header">Assignment Status</th>
                  <th className="md-table-header">Technology</th>
                  <th className="md-table-header">IT Infrastructure</th>
                  <th className="md-table-header">Application Dependencies</th>
                  <th
                    className="md-table-header sortable"
                    onClick={() => handleSort('recommendation')}
                  >
                    Final Recommendation
                    {sortField === 'recommendation' && (
                      <span className="sort-indicator">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th className="md-table-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedApplications.map((app) => (
                  <tr key={app.application_id_code} className="md-table-row">
                    <td className="md-table-cell">
                      <div className="md-app-info">
                        <div className="md-app-info__primary">{app.application_id_code}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span className="md-app-info__primary">{app.application_name}</span>
                          {(app as any).license_renegotiation && (
                            <span style={{ ...WS_CHIP, ...wsTone('warning') }}>
                              License Renegotiation
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="md-table-cell">
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                        {assignedApplications.has(app.application_id_code) ? (
                          <>
                            <span style={{ ...WS_CHIP, ...wsTone('warning') }}>
                              <Icon icon={icons.Layers} size={16} /> Assigned
                            </span>
                            {/* Retain and Re-Design apps can be shared across multiple groups */}
                            {(app.recommendation === 'Retain' || app.recommendation === 'Re-Design') && (
                              <span style={{ ...WS_CHIP, ...wsTone('success') }}>
                                <Icon icon={icons.Plus} size={16} /> Available
                              </span>
                            )}
                          </>
                        ) : (
                          <span style={{ ...WS_CHIP, ...wsTone('success') }}>
                            <Icon icon={icons.CheckCircle2} size={16} /> Available
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="md-table-cell">
                      <span className="technology-info">
                        {app.technology_stack || 'Not specified'}
                      </span>
                    </td>
                    <td className="md-table-cell">
                      <span className="infrastructure-info">
                        {app.it_hardware_infrastructure || 'Not specified'}
                      </span>
                    </td>
                    <td className="md-table-cell">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {app.application_dependencies === 'YES' ? (
                          <span style={{ ...WS_CHIP, ...wsTone('info') }}>
                            <Icon icon={icons.Check} size={16} /> YES
                          </span>
                        ) : (
                          <span style={{ ...WS_CHIP, ...wsTone('neutral') }}>
                            <Icon icon={icons.Minus} size={16} /> NO
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="md-table-cell">
                      <div
                        className="md-chip"
                        style={{
                          backgroundColor: getRecommendationColor(app.recommendation),
                          color: matrixColor(app.recommendation).onBase
                        }}
                      >
                        {app.final_disposition || app.recommendation}
                      </div>
                    </td>
                    <td className="md-table-cell">
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                        {/* Show only View button if application is assigned to Integration Group */}
                        {assignedApplications.has(app.application_id_code) ? (
                          <IconButton
                            icon={icons.Eye}
                            label="View application details (Read-only: Application is assigned to Integration Group)"
                            title="View application details (Read-only: Application is assigned to Integration Group)"
                            onClick={() => handleOpenViewModal(app)}
                          />
                        ) : (
                          // Show both Change and View buttons if not assigned
                          <>
                            <IconButton
                              icon={icons.Eye}
                              label="View application details"
                              title="View application details"
                              onClick={() => handleOpenViewModal(app)}
                            />
                            <IconButton
                              icon={icons.Pencil}
                              label="Change application details"
                              title="Change application details"
                              onClick={() => handleOpenChangeModal(app)}
                            />
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="md-empty-state">
              <div className="md-empty-icon"><Icon icon={icons.ClipboardList} size={24} /></div>
              <h3>No Applications Found</h3>
              <p>No applications have been transferred to integration yet.</p>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="md-pagination">
          <button 
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className="md-button--secondary"
          >
            First
          </button>
          <button 
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="md-button--secondary"
          >
            Previous
          </button>
          <span className="md-pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button 
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="md-button--secondary"
          >
            Next
          </button>
          <button 
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className="md-button--secondary"
          >
            Last
          </button>
        </div>
      )}

      </div>

      {/* Change — a right-side panel (Decision Workspace Sheet, single width). */}
      <Sheet
        open={showChangeModal && !!selectedApplication}
        title="Change Application Details"
        onClose={handleCloseChangeModal}
        width={531}
        actions={
          <>
            <button type="button" className="btn" onClick={handleCloseChangeModal}>Cancel</button>
            <button type="submit" form="iamd-change-form" className="btn btn-primary">Save Changes</button>
          </>
        }
      >
        {selectedApplication && (
          <ChangeApplicationForm
            application={selectedApplication}
            onSave={handleCloseChangeModal}
            onCancel={handleCloseChangeModal}
          />
        )}
      </Sheet>

      {/* View — a right-side panel (Decision Workspace Sheet, single width). */}
      <Sheet
        open={showViewModal && !!selectedApplication}
        title="View Application Details"
        onClose={handleCloseViewModal}
        width={531}
      >
        {selectedApplication && <ViewApplicationDetails application={selectedApplication} />}
      </Sheet>
    </div>
  );
};

// Change Application Form Component
interface ChangeApplicationFormProps {
  application: ApplicationMaster;
  onSave: () => void;
  onCancel: () => void;
}

const ChangeApplicationForm: React.FC<ChangeApplicationFormProps> = ({ application, onSave }) => {
  const [formData, setFormData] = useState({
    application_dependencies: application.application_dependencies || 'NO'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken');
      const response = await authFetch(`/api/applications-master/${application.application_id_code}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to save changes');
      }

      onSave();
    } catch (err: any) {
      setError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form id="iamd-change-form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {error && <Banner tone="error">{error}</Banner>}
      {saving && <Banner tone="info">Saving...</Banner>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Read-only fields */}
        <section>
          <h3>Application Information (Read-only)</h3>
          <FactList
            items={[
              { label: 'Application ID:', value: application.application_id_code },
              { label: 'Application Name:', value: application.application_name },
              { label: 'Technology Stack:', value: application.technology_stack || 'Not specified' },
              { label: 'IT Infrastructure:', value: application.it_hardware_infrastructure || 'Not specified' },
              {
                label: 'Final Recommendation:',
                value: (
                  <span style={{ ...WS_CHIP, background: matrixColor(application.recommendation).base, color: matrixColor(application.recommendation).onBase }}>
                    {application.final_disposition || application.recommendation}
                  </span>
                ),
              },
            ]}
          />
        </section>

        {/* Editable fields */}
        <section>
          <h3>Editable Dependencies</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label htmlFor="iamd-deps">Application Dependencies:</label>
            <select
              id="iamd-deps"
              value={formData.application_dependencies}
              onChange={(e) => setFormData({ ...formData, application_dependencies: e.target.value as 'YES' | 'NO' })}
            >
              <option value="YES">YES</option>
              <option value="NO">NO</option>
            </select>
          </div>
        </section>
      </div>

      {/* Cancel and Save Changes are in the panel's fixed action row (Sheet `actions`). */}
    </form>
  );
};

// View Application Details Component
interface ViewApplicationDetailsProps {
  application: ApplicationMaster;
}

const ViewApplicationDetails: React.FC<ViewApplicationDetailsProps> = ({ application }) => {
  return (
    <FactList
      items={[
        { label: 'Application ID:', value: application.application_id_code },
        { label: 'Application Name:', value: application.application_name },
        { label: 'Technology Stack:', value: application.technology_stack || 'Not specified' },
        { label: 'IT Infrastructure:', value: application.it_hardware_infrastructure || 'Not specified' },
        {
          label: 'Application Dependencies:',
          value: (
            <span style={{ ...WS_CHIP, ...wsTone(application.application_dependencies === 'YES' ? 'success' : 'neutral') }}>
              {application.application_dependencies || 'NO'}
            </span>
          ),
        },
        {
          label: 'Final Recommendation:',
          value: (
            <span style={{ ...WS_CHIP, background: matrixColor(application.recommendation).base, color: matrixColor(application.recommendation).onBase }}>
              {application.final_disposition || application.recommendation}
            </span>
          ),
        },
        {
          label: 'License Renegotiation:',
          value: (
            <span style={{ ...WS_CHIP, ...wsTone((application as any).license_renegotiation ? 'warning' : 'neutral') }}>
              {(application as any).license_renegotiation ? 'YES — Eligible for License Renegotiation' : 'NO'}
            </span>
          ),
        },
        { label: 'Created:', value: application.created_at_formatted },
      ]}
    />
  );
};

export default IntegrationApplicationMasterData;
